import { Suspense } from 'react';
import { Outlet } from 'react-router-dom';
import { AppShell } from '@/components/shell/app-shell';
import { CommandPalette } from '@/components/shell/command-palette';
import { ConsoleSidebar } from '@/components/shell/console-sidebar';
import { FleetSwitcher } from '@/components/shell/fleet-switcher';
import { HeaderUserProfile } from '@/components/shell/header-user-profile';
import { NotificationsMenu } from '@/components/shell/notifications-menu';
import { SearchTrigger } from '@/components/shell/search-trigger';
import { Skeleton } from '@/components/ui/skeleton';
import { usePaletteStore } from '@/stores/paletteStore';
import { useWorkspaceStore } from '@/stores/workspaceStore';

/**
 * The logged-in frame, persistent across navigation: AppShell + the rail +
 * the top bar. Route chunks load inside it, so a lazy page never unmounts
 * the sidebar or the top bar.
 *
 * Geometry comes from CSS vars on :root (theme.css), driven by the rail's
 * data-sidebar-mode: --app-content-inset (content margin) and
 * --app-topnav-h (top bar height).
 */
export function ConsoleLayout() {
  const togglePalette = usePaletteStore((s) => s.toggle);
  const workspaceId = useWorkspaceStore((s) => s.workspaceId);

  return (
    <AppShell
      sidebar={<ConsoleSidebar />}
      topBar={
        <>
          <FleetSwitcher />
          <div className="flex-1" />
          <SearchTrigger onOpen={togglePalette} />
          <div className="flex items-center gap-1">
            <NotificationsMenu />
            <HeaderUserProfile />
          </div>
        </>
      }
    >
      <CommandPalette />
      <div className="min-h-0 flex-1 overflow-hidden">
        <Suspense fallback={<PageFallback />}>
          <Outlet key={workspaceId} />
        </Suspense>
      </div>
    </AppShell>
  );
}

/** Appears only if a chunk takes longer than ~200ms, so fast loads show nothing. */
function PageFallback() {
  return (
    <div className="mx-auto w-full max-w-6xl animate-[fade-in_160ms_ease-out_200ms_both] px-4 pt-8 md:px-8">
      <Skeleton className="h-7 w-48" />
      <Skeleton className="mt-3 h-4 w-72" />
      <Skeleton className="mt-8 h-24 w-full" />
      <Skeleton className="mt-4 h-72 w-full" />
    </div>
  );
}
